import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    Divider,
    FormControlLabel,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DialogHeader from "../../../components/Dialog/DialogHeader";
import UserDialog from "../../../components/Dialog/UserDialog";
import { SaveSkillBranchDto, SaveSkillDto, SkillBranchDict } from "../../../model/Dto/Dict/Skill";

interface LocalSkill {
    uid: string;
    id?: string;
    name: string;
    description: string;
}

function makeUid() {
    return Math.random().toString(36).slice(2);
}

interface SortableSkillRowProps {
    skill: LocalSkill;
    index: number;
    loading: boolean;
    onChange: (uid: string, field: "name" | "description", value: string) => void;
    onRemove: (uid: string) => void;
}

function SortableSkillRow({ skill, index, loading, onChange, onRemove }: SortableSkillRowProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: skill.uid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : "auto",
        position: "relative" as const,
    };

    return (
        <TableRow ref={setNodeRef} style={style} hover>
            <TableCell padding="none" width={32} align="center">
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{ cursor: isDragging ? "grabbing" : "grab", display: "flex", justifyContent: "center", px: 0.5 }}
                >
                    <DragIndicatorIcon fontSize="small" sx={{ color: "text.disabled" }} />
                </Box>
            </TableCell>
            <TableCell width={40}>
                <Typography variant="body2" color="text.secondary">{index + 1}</Typography>
            </TableCell>
            <TableCell>
                <TextField
                    value={skill.name}
                    onChange={e => onChange(skill.uid, "name", e.target.value)}
                    size="small"
                    fullWidth
                    disabled={loading}
                    placeholder="Название навыка"
                />
            </TableCell>
            <TableCell>
                <TextField
                    value={skill.description}
                    onChange={e => onChange(skill.uid, "description", e.target.value)}
                    size="small"
                    fullWidth
                    disabled={loading}
                    placeholder="Описание (необязательно)"
                    multiline
                    maxRows={3}
                />
            </TableCell>
            <TableCell padding="none" width={48} align="center">
                <IconButton size="small" color="error" onClick={() => onRemove(skill.uid)} disabled={loading}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

interface SkillBranchDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: SaveSkillBranchDto) => Promise<boolean>;
    initialData?: SkillBranchDict | null;
    title: string;
}

export default function SkillBranchDialog({ open, onClose, onSave, initialData, title }: SkillBranchDialogProps) {
    const [name, setName] = useState("");
    const [order, setOrder] = useState(0);
    const [isPsiPower, setIsPsiPower] = useState(false);
    const [skills, setSkills] = useState<LocalSkill[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        if (open) {
            setError(null);
            if (initialData) {
                setName(initialData.name);
                setOrder(initialData.order);
                setIsPsiPower(initialData.isPsiPower);
                setSkills(
                    [...initialData.skills]
                        .sort((a, b) => a.order - b.order)
                        .map(s => ({ uid: makeUid(), id: s.id, name: s.name, description: s.description }))
                );
            } else {
                setName("");
                setOrder(0);
                setIsPsiPower(false);
                setSkills([]);
            }
        }
    }, [open, initialData]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setSkills(prev => {
                const oldIndex = prev.findIndex(s => s.uid === active.id);
                const newIndex = prev.findIndex(s => s.uid === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const addSkill = () => setSkills(prev => [...prev, { uid: makeUid(), name: "", description: "" }]);

    const removeSkill = (uid: string) => setSkills(prev => prev.filter(s => s.uid !== uid));

    const updateSkill = (uid: string, field: "name" | "description", value: string) =>
        setSkills(prev => prev.map(s => s.uid === uid ? { ...s, [field]: value } : s));

    const handleSave = async () => {
        if (!name.trim()) { setError("Введите название ветки навыков"); return; }
        if (skills.some(s => !s.name.trim())) { setError("Заполните названия всех навыков"); return; }

        setLoading(true);
        setError(null);

        const dto: SaveSkillBranchDto = {
            name: name.trim(),
            order,
            isPsiPower,
            skills: skills.map<SaveSkillDto>((s, i) => ({
                id: s.id,
                name: s.name.trim(),
                description: s.description.trim(),
                order: i + 1,
            })),
        };

        const success = await onSave(dto);
        setLoading(false);
        if (success) onClose();
    };

    const handleClose = () => { setError(null); onClose(); };

    return (
        <UserDialog handleClose={handleClose} open={open} maxWidth="md" error={error}>
            <DialogHeader loading={loading} title={title} />
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <TextField
                            label="Название ветки навыков"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            fullWidth
                            disabled={loading}
                            autoFocus
                        />
                        <TextField
                            label="Порядок"
                            type="number"
                            value={order}
                            onChange={e => setOrder(Number(e.target.value))}
                            disabled={loading}
                            sx={{ width: 110 }}
                            inputProps={{ min: 0 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isPsiPower}
                                    onChange={e => setIsPsiPower(e.target.checked)}
                                    disabled={loading}
                                />
                            }
                            label="Психосилы"
                            sx={{ whiteSpace: "nowrap" }}
                        />
                    </Box>

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Навыки</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={addSkill} disabled={loading}>
                                Добавить навык
                            </Button>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {skills.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                                Нет навыков. Нажмите «Добавить навык».
                            </Typography>
                        ) : (
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={skills.map(s => s.uid)} strategy={verticalListSortingStrategy}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width={32} padding="none" />
                                                <TableCell width={40}>#</TableCell>
                                                <TableCell>Название</TableCell>
                                                <TableCell>Описание</TableCell>
                                                <TableCell width={48} padding="none" />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {skills.map((skill, i) => (
                                                <SortableSkillRow
                                                    key={skill.uid}
                                                    skill={skill}
                                                    index={i}
                                                    loading={loading}
                                                    onChange={updateSkill}
                                                    onRemove={removeSkill}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </SortableContext>
                            </DndContext>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Отмена</Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {loading ? "Сохранение..." : "Сохранить"}
                </Button>
            </DialogActions>
        </UserDialog>
    );
}
